import { useMemo, useState, useEffect } from 'react'
import { DateTime } from 'luxon'
import {
  getCategoryData,
  filterChartAPIResponse,
  processDataForChart,
  ChartAPIResponse,
  getInsightsDateRange,
  INSIGHTS_TYPE,
  filterChartCategories,
  insightsCopyConstants,
  insightsTrackDefaultFilters,
  getExampleExpenseChartData,
} from './utils'
import { InsightsChart } from './InsightsChart'
import {
  fetchInsightsBarchartData,
  FETCH_INSIGHTS_BARCHART_DATA_KEY,
} from './financeActions'
import { Text, Badge, Card, Select, SelectDropdownOption } from '@/ui'
import { TextColors, TypographyStyles } from '@/styles'

import {
  useEndDateForFinancialInsights,
  useReselector,
} from '@/utils/sharedHooks'
import { getFetchError, selectIsFetchingForKeys } from '@/reducers/fetch'
import { DATE_FORMATS_LUXON } from '@/utils/dateHelpers'
import { isEmpty } from 'lodash'
import { useAppDispatch } from '@/utils/typeHelpers'
import { FreeTrialUnreviewedAlert } from '@/components/FreeTrialUnreviewedAlert'
// TODO: Migrate V2  skipcq: JS-0099
import { Loader } from '@/componentsV1/BaseComponents'

export const TrackInsightsSection = ({
  inputDate,
  type,
  shouldDisplaySampleData = false,
}: {
  inputDate?: string
  type: INSIGHTS_TYPE
  shouldDisplaySampleData?: boolean
}) => {
  const dispatch = useAppDispatch()

  const [responseData, setResponseData] = useState<ChartAPIResponse[]>()
  const [filter, setFilter] = useState('all')

  const currentDate = useMemo(
    () =>
      inputDate
        ? DateTime.fromFormat(inputDate, DATE_FORMATS_LUXON.INPUT)
        : DateTime.now(),
    [inputDate]
  )

  const endDate = useEndDateForFinancialInsights(currentDate)

  const dateRange = useMemo(() => getInsightsDateRange(endDate), [endDate])

  useEffect(() => {
    const fetchData = async (range: [string, string]) => {
      const [startDate, endDate] = range
      const data = shouldDisplaySampleData
        ? getExampleExpenseChartData()
        : await fetchInsightsBarchartData(type, startDate, endDate)(dispatch)
      setResponseData(data)
    }
    if (dateRange) {
      fetchData(dateRange)
    }
  }, [dispatch, dateRange, type, shouldDisplaySampleData])

  const { chartCategories, topCategories } = useMemo(() => {
    if (!responseData)
      return {
        chartCategories: null,
        topCategories: [],
      }
    return getCategoryData(responseData)
  }, [responseData])

  const filteredData = useMemo(() => {
    if (!responseData) return []

    return filterChartAPIResponse(responseData, filter, topCategories)
  }, [responseData, filter, topCategories])

  const filteredChartCategories = useMemo(() => {
    if (!chartCategories) return {}
    return filterChartCategories(chartCategories, filter, topCategories)
  }, [chartCategories, filter, topCategories])

  const chartData = useMemo(() => {
    return processDataForChart(filteredData, dateRange, type)
  }, [filteredData, dateRange, type])

  const dropdownOptions = useMemo(() => {
    const options = [...insightsTrackDefaultFilters[type]]
    if (!chartCategories) return options
    Object.entries(chartCategories).forEach(([identifier, category]) => {
      options.push({
        text: category.category_name ?? '',
        value: identifier ?? '',
      })
    })
    return options
  }, [chartCategories, type])

  const dateRangeString = useMemo(() => {
    if (!dateRange) return ''
    const [startDate, endDate] = dateRange
    const start = DateTime.fromFormat(
      startDate,
      DATE_FORMATS_LUXON.TIMESTAMP
    ).toFormat(DATE_FORMATS_LUXON.MONTH_YEAR)
    const end = DateTime.fromFormat(
      endDate,
      DATE_FORMATS_LUXON.TIMESTAMP
    ).toFormat(DATE_FORMATS_LUXON.MONTH_YEAR)
    return `Last 12 months (${start} - ${end})`
  }, [dateRange])

  const queryLoading = useReselector(selectIsFetchingForKeys, [
    FETCH_INSIGHTS_BARCHART_DATA_KEY,
  ])

  const insightsBarchartDataError = useReselector(
    getFetchError,
    FETCH_INSIGHTS_BARCHART_DATA_KEY
  )

  const isChartDataEmpty = isEmpty(filteredData)

  return (
    <Card>
      <div className="w-full flex flex-col sm:flex-row flex-wrap items-center justify-between gap-4">
        <Text variant={TypographyStyles.H2} className="sm:w-1/2">
          {insightsCopyConstants[type].trackPageTitle}{' '}
          {shouldDisplaySampleData && (
            <Badge color="orange" className="rounded-md ml-2">
              Sample data
            </Badge>
          )}
        </Text>

        <Text
          variant={TypographyStyles.BODY_SM}
          className="sm:w-1/3 text-right"
          color={TextColors.MUTED}
        >
          {dateRangeString}
        </Text>

        <div className="sm:w-1/4 min-w-[180px] text-right">
          {type !== INSIGHTS_TYPE.PROFIT && (
            <Select
              value={filter}
              options={dropdownOptions as SelectDropdownOption<string>[]}
              onValueChange={(val) => setFilter(val ?? '')}
              // TODO: Add divider after
              // dividerAfter={type === INSIGHTS_TYPE.EXPENSES ? [1] : [0]}
            />
          )}
        </div>
      </div>

      <div className="w-full pt-4">
        <FreeTrialUnreviewedAlert />
      </div>

      {queryLoading ? (
        <div className="w-full">
          <Loader loading />
        </div>
      ) : (
        <div className="w-full pt-4">
          {insightsBarchartDataError ? (
            <div className="text-center space-y-2">
              <Text variant={TypographyStyles.H3}>Something Went Wrong</Text>
              <Text variant={TypographyStyles.BODY_SM} color={TextColors.MUTED}>
                Please try again in a bit.
              </Text>
            </div>
          ) : (
            <InsightsChart
              data={chartData}
              categories={chartCategories || {}}
              filteredCategories={filteredChartCategories || {}}
              filter={filter}
              isChartDataEmpty={isChartDataEmpty}
              type={type}
            />
          )}
        </div>
      )}
    </Card>
  )
}
